import React from "react";
import { useSnapshot } from "valtio";
import { baseURL } from "@pages/Applications/C&C/C&CSetup/Website/constants";
import { templateStore } from "@pages/Applications/C&C/C&CSetup/templateStore";

interface FileData {
    fileObject?: File;
    Url: string;
    Name: string;
    Type: string;
    Extenxion: string;
}

interface MediaFile extends FileData {}
export const languages: Language[] = [
    {
        key: "FR",
        label: "Français",
        active: true,
        flag: "https://flagpedia.net/data/flags/h80/fr.webp",
        id: "5c2ce585-68ca-49c8-9fda-d64f1e9fba40",
        isDefault: true,
        language: "French",
    },
    {
        key: "EN",
        label: "Anglais",
        active: true,
        flag: "https://flagpedia.net/data/flags/h80/gb.webp",
        id: "3de90539-c727-40aa-a9a2-af84be160223",
        isDefault: true,
        language: "English",
    },
    {
        key: "ES",
        label: "Espagnol",
        active: true,
        flag: "https://flagpedia.net/data/flags/h80/es.webp",
        id: "0d436099-4cf6-4f26-9d2f-372d9a0207c5",
        isDefault: true,
        language: "Spanish",
    },
    {
        key: "IT",
        label: "Italien",
        active: true,
        flag: "https://flagpedia.net/data/flags/h80/it.webp",
        id: "dfa2b4f6-9bf3-4eb4-a171-0707deff50f3",
        isDefault: true,
        language: "Italian",
    },
    {
        key: "AL",
        label: "Allemand",
        active: true,
        flag: "https://flagpedia.net/data/flags/h80/de.webp",
        id: "bb13c224-e4ae-493b-b8d5-1ab880e5ff2e",
        isDefault: true,
        language: "German",
    },
];

export interface Language {
    key: LanguageKey;
    label: string;
    active: boolean;
    flag: string;
    id: string;
    isDefault: boolean;
    language: string;
}

export type LanguageKey = "FR" | "EN" | "ES" | "IT" | "AL";

export function handleImageUploadLogic(
    path: string[],
    type?: "Mobile" | "Desktop",
    e: React.ChangeEvent<HTMLInputElement>,
    updateMediaFile: (
        path: string[],
        field: keyof MediaFile,
        value: File | string
    ) => void,
    section?: any
): void {
    const file = e.target.files?.[0];
    if (!file) return;

    updateMediaFile(
        section !== undefined || type === undefined
            ? [...path]
            : [...path, type],
        "fileObject",
        file
    );

    const previewUrl = URL.createObjectURL(file);
    updateMediaFile(
        section !== undefined || type === undefined
            ? [...path]
            : [...path, type],
        "Url",
        previewUrl
    );
}
export function handleTextChangeLogic(
    path: string[],
    field: string,
    value: string,
    activeLanguage: string,
    updateLanguageContent: (
        path: string[],
        language: string,
        value: string
    ) => void
): void {
    const fullPath = [...path, field];
    updateLanguageContent(fullPath, activeLanguage, value);
}
export const getNestedValue = (path: string[]) => {
    let current: any = templateStore.template;
    for (const key of path) {
        current = current?.[key];
        if (!current) return undefined;
    }
    return current;
};

export const getImageUrl = (path: string[], type: "Desktop" | "Mobile") => {
    return getNestedValue([...path, type, "Url"]);
};

export const useTemplateData = () => {
    const snap = useSnapshot(templateStore);

    const getNestedValue = <T>(path: string[]): T | null => {
        try {
            let current: any = snap.template;
            for (const key of path) {
                if (!current || typeof current !== "object") return null;
                current = current[key];
            }
            return current as T;
        } catch (error) {
            console.error("Error accessing nested value:", error);
            return null;
        }
    };

    const getImageUrlDesktop = (path: string[], iuud: string) => {
        const desktopImageUrl =
            getNestedValue<string>([...path, "Desktop", "Url"]) || "";
        if (!desktopImageUrl) return "";
        if (desktopImageUrl.startsWith("blob:")) return desktopImageUrl;
        return `${baseURL}/${iuud}/${desktopImageUrl}`;
    };
    const getImageUrlMobile = (path: string[], iuud: string) => {
        const mobileImageUrl =
            getNestedValue<string>([...path, "Mobile", "Url"]) || "";
        if (!mobileImageUrl) return "";
        if (mobileImageUrl.startsWith("blob:")) return mobileImageUrl;
        return `${baseURL}/${iuud}/${mobileImageUrl}`;
    };
    const getImageUrl = (path: string[], iuud: string) => {
        const imageUrl = getNestedValue<string>([...path, "Url"]) || "";
        if (!imageUrl) return "";
        if (imageUrl.startsWith("blob:")) return imageUrl;
        return `${baseURL}/${iuud}/${imageUrl}`;
    };
    const getImageName = (path: string[]) => {
        const imageName = getNestedValue<string>([...path, "Name"]) || "";
        if (!imageName) return "";
        return imageName;
    };

    return {
        getNestedValue,
        getImageUrlDesktop,
        getImageUrlMobile,
        getImageUrl,
        getImageName,
        iuud: snap.iuud,
    };
};

export const templateDefaultValue: any = {
    SiteWeb: {
        Payment: {
            Default: {
                Header: {
                    Title: {
                        AL: "Zahlen Sie schnell und sicher!",
                        EN: "Pay quickly and securely!",
                        ES: "¡Paga rápido y seguro!",
                        FR: "Payez rapidement et en toute sécurité !",
                        IT: "Paga in modo rapido e sicuro!",
                    },
                },
            },
        },
        Profile: {
            Default: {
                Header: {
                    Title: {
                        AL: "Erkunde und verwalte dein Konto!",
                        EN: "Explore and manage your account!",
                        ES: "¡Explora y gestiona tu cuenta!",
                        FR: "Explorez et gérez votre compte !",
                        IT: "Esplora e gestisci il tuo account!",
                    },
                },
            },
        },
        LaindingPage: {
            Bannier: {
                Title: {
                    AL: "",
                    EN: "",
                    ES: "",
                    FR: "",
                    IT: "",
                },
                Description: {
                    AL: "",
                    EN: "",
                    ES: "",
                    FR: "",
                    IT: "",
                },
            },
            Section1: {
                Title: {
                    AL:
                        "Willkommen auf unserer Seite! Entdecken, bestellen und genießen Sie Ihre Mahlzeiten.",
                    EN:
                        "Welcome to our Website! Discover, order, and enjoy your meals.",
                    ES:
                        "¡Bienvenido a nuestro sitio! Descubre, ordena y disfruta tus comidas.",
                    FR:
                        "Bienvenue sur notre site ! Découvrez, commandez et savourez vos repas.",
                    IT:
                        "Benvenuto sul nostro sito! Scopri, ordina e gusta i tuoi pasti.",
                },
            },
            Section2: {
                Title: {
                    AL: "Unsere aktuellen Angebote",
                    EN: "Our current offers",
                    ES: "Nuestras ofertas actuales",
                    FR: "Nos offres du moment",
                    IT: "Le nostre offerte attuali",
                },
            },
            Section3: {
                Title: {
                    AL: "Finden Sie das nächstgelegene Geschäft!",
                    EN: "Find the closest store to you!",
                    ES: "¡Encuentra la tienda más cercana a ti!",
                    FR: "Trouvez le magasin le plus proche de chez vous !",
                    IT: "Trova il negozio più vicino a te!",
                },
                Description: {
                    AL:
                        "Verwenden Sie unseren Filialfinder, um schnell den nächstgelegenen Standort zu finden und alle benötigten Informationen zu erhalten!",
                    EN:
                        "Use our store locator to quickly find the nearest location and get all the details you need!",
                    ES:
                        "Usa nuestro localizador de tiendas para encontrar rápidamente la ubicación más cercana y obtener todos los detalles que necesitas.",
                    FR:
                        "Utilisez notre localisateur de magasins pour trouver rapidement le point de vente le plus proche et obtenir toutes les informations dont vous avez besoin !",
                    IT:
                        "Usa il nostro localizzatore per trovare rapidamente il negozio più vicino e ottenere tutte le informazioni di cui hai bisogno!",
                },
            },
            Section4: {
                Title: {
                    AL:
                        "Bestel moeiteloos bij je favoriete restaurant via onze mobiele app!",
                    EN:
                        "Order from your favorite restaurant with ease using our mobile app!",
                    ES:
                        "Pide fácilmente de tu restaurante favorito con nuestra app móvil.",
                    FR:
                        "Commandez facilement depuis votre restaurant préféré grâce à notre application mobile !",
                    IT:
                        "Ordina facilmente dal tuo ristorante preferito con la nostra app mobile!",
                },
                Description: {
                    AL:
                        "Geniet van een soepele bestelervaring bij je favoriete restaurant met onze gebruiksvriendelijke app.",
                    EN:
                        "Enjoy a seamless ordering experience from your favorite restaurant with our easy-to-use mobile app!",
                    ES:
                        "Disfruta de una experiencia de pedido fluida desde tu restaurante favorito con nuestra app fácil de usar.",
                    FR:
                        "Profitez d'une expérience de commande fluide depuis votre restaurant préféré grâce à notre application facile à utiliser !",
                    IT:
                        "Goditi un’esperienza di ordinazione senza interruzioni dal tuo ristorante preferito con la nostra app facile da usare!",
                },
            },
        },
        ChoixBoutique: {
            Header: {
                Title: {
                    AL: "Entdecken Sie unsere Geschäfte!",
                    EN: "Explore our stores!",
                    ES: "¡Explora nuestras tiendas!",
                    FR: "Découvrez nos boutiques !",
                    IT: "Esplora i nostri negozi!",
                },
                Description: {
                    AL:
                        "Entdecken und erkunden Sie unsere Geschäfte für ein einzigartiges Einkaufserlebnis!",
                    EN:
                        "Discover and explore our stores for a unique shopping experience!",
                    ES:
                        "¡Descubre y explora nuestras tiendas para una experiencia de compra única!",
                    FR:
                        "Découvrez et explorez nos boutiques pour une expérience de shopping unique!",
                    IT:
                        "Scopri ed esplora i nostri negozi per un'esperienza di shopping unica!",
                },
            },
        },
        PriseCommande: {
            Default: {
                Header: {
                    Title: {
                        AL:
                            "Entdecke unsere Speisekarte und finde dein perfektes Gericht!",
                        EN: "Explore our menu and find your ideal dish!",
                        ES: "Explora nuestro menú y encuentra tu plato ideal.",
                        FR: "Explorez notre menu et trouvez votre plat idéal !",
                        IT:
                            "Esplora il nostro menu e trova il tuo piatto ideale!",
                    },
                },
            },
        },
    },
    Application: {
        Loyalty: {
            Default: {
                Solde: {
                    Title: {
                        AL: "Ihre Punkte, Ihr Vergnügen!",
                        EN: "Your points, your pleasures!",
                        ES: "¡Tus puntos, tus placeres!",
                        FR: "Vos points, vos plaisirs!",
                        IT: "I tuoi punti, i tuoi piaceri!",
                    },
                    Info_1: {
                        AL: "Du hast",
                        EN: "You have",
                        ES: "Tienes",
                        FR: "Vous avez",
                        IT: "Hai",
                    },
                    Info_2: {
                        AL: "punkte",
                        EN: "points",
                        ES: "puntos",
                        FR: "points",
                        IT: "punti",
                    },
                    BottomTitle: {
                        AL: "Mein Treuepass anzeigen",
                        EN: "Show my Loyalty Pass",
                        ES: "Mostrar mi Pase de Fidelidad",
                        FR: "Afficher mon Pass fidélité",
                        IT: "Mostra il mio Pass Fedeltà",
                    },
                },
                Avantages: {
                    Title: {
                        AL: "Entsperre deine Treue-Vergnügungen",
                        EN: "Unlock your loyalty rewards",
                        ES: "Desbloquea tus placeres de fidelidad",
                        FR: "Débloque tes plaisirs fidélité",
                        IT: "Sblocca i tuoi premi fedeltà",
                    },
                    Info_1: {
                        AL: "Du hast noch",
                        EN: "You still have",
                        ES: "Te queda",
                        FR: "Il te reste",
                        IT: "Ti resta",
                    },
                    Info_2: {
                        AL: "punkte",
                        EN: "points",
                        ES: "puntos",
                        FR: "points",
                        IT: "punti",
                    },
                },
                Recompenses: {
                    Title: {
                        AL: "Genieße deine Geschenke",
                        EN: "Enjoy your gifts",
                        ES: "Disfruta de tus regalos",
                        FR: "Fais-toi plaisir avec tes cadeaux",
                        IT: "Divertiti con i tuoi regali",
                    },
                    BottomTitle: {
                        AL: "Meine Belohnungen und Gutscheincodes",
                        EN: "My rewards and promo codes",
                        ES: "Mis recompensas y códigos promocionales",
                        FR: "Mes récompenses et Codes promo",
                        IT: "Le mie ricompense e codici promozionali",
                    },
                },
            },
        },
        LaindingPage: {
            Default: {
                Head: {
                    Title: {
                        AL:
                            "Willkommen in unserer App! Entdecken, bestellen und genießen Sie Ihre Mahlzeiten.",
                        EN:
                            "Welcome to our app! Discover, order and enjoy your meals.",
                        ES:
                            "Bienvenido a nuestra aplicación. Descubre, ordena y disfruta tus comidas.",
                        FR:
                            "Bienvenue sur notre app ! Découvrez, commandez et savourez vos repas.",
                        IT:
                            "Benvenuto nella nostra app! Scopri, ordina e gusta i tuoi pasti.",
                    },
                },
                Offres: {
                    Title: {
                        AL: "Unsere aktuellen Angebote",
                        EN: "Our current offers",
                        ES: "Nuestras ofertas actuales",
                        FR: "Nos offres du moment",
                        IT: "Le nostre offerte attuali",
                    },
                },
                Loyalty: {
                    Title1: {
                        AL: "Alle Treuepunkte-Shops",
                        EN: "All loyalty shops",
                        ES: "Todas las tiendas de fidelización",
                        FR: "Toutes les boutiques fidélité",
                        IT: "Tutti i negozi fedeltà",
                    },
                    Title2: {
                        AL: "100€ Einkauf = 2€ geschenkt",
                        EN: "€100 purchase = €2 free",
                        ES: "100€ de compra = 2€ de regalo",
                        FR: "100€ d'achat = 2€ offerts",
                        IT: "100€ di acquisto = 2€ omaggio",
                    },
                },
                BestProducts: {
                    Title: {
                        AL: "Unsere besten Produkte",
                        EN: "Our best products",
                        ES: "Nuestros mejores productos",
                        FR: "Nos meilleurs produits",
                        IT: "I nostri migliori prodotti",
                    },
                },
            },
        },
        ChoixBoutique: {
            Default: {
                Description: {
                    FR:
                        "Bienvenue dans notre boutique ! Découvrez nos produits préparés avec soin, à emporter ou en click & collect, pour régaler vos envies à tout moment.",
                    EN:
                        "Welcome to our store! Discover our carefully prepared products, available for takeaway or click & collect, to satisfy your cravings anytime.",
                    ES:
                        "¡Bienvenido a nuestra tienda! Descubre nuestros productos cuidadosamente preparados, disponibles para llevar o con click & collect, para satisfacer tus antojos en cualquier momento.",
                    IT:
                        "Benvenuto nel nostro negozio! Scopri i nostri prodotti preparati con cura, disponibili da asporto o con click & collect, per soddisfare le tue voglie in ogni momento.",
                    AL:
                        "Willkommen in unserem Geschäft! Entdecken Sie unsere mit Sorgfalt zubereiteten Produkte, zum Mitnehmen oder per Click & Collect – perfekt für jeden Hunger, jederzeit.",
                },
            },
        },
    },
};
